import React, { useState, useEffect } from "react";
import { Button, Form, FormGroup, Input, Label, Alert } from "reactstrap";
import axios from "axios";
import Select from "react-select";
import { API_URL_subproyectos, API_URL_batch_subproyectos } from "../../constants";

import { getAuthHeaders } from "../../utils/auth";
const NewBatchSubproyectoForm = ({ toggle, resetState, batch }) => {
  const [subprojects, setProjects] = useState([]);
  const [batchProjects, setBatchProjects] = useState([]);
  const [batchName, setBatchName] = useState("");
  const [batchSecondName, setBatchSecondName] = useState("");
  const [batchSecondDate, setBatchSecondDate] = useState("");
  const [remainingPercentage, setRemainingPercentage] = useState(100);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Check if we're editing or cloning or creating
  // We'll assume if batch && !batch.isClone => "edit"
  //            if batch && batch.isClone  => "clone"
  //            if !batch                 => "create"
  const isCloneMode = batch?.isClone === true;
  const isEditMode = batch && !isCloneMode;
  const formTitle = isCloneMode
    ? "Clonar Bundle"
    : isEditMode
    ? "Editar Bundle"
    : "Crear Nuevo Bundle";

  useEffect(() => {
    const headers = getAuthHeaders();

    // Fetch subprojects for the dropdown
    axios
      .get(API_URL_subproyectos, { headers })
      .then((response) => {
        setProjects(
          response.data.map((p) => ({
            value: p.pk,
            label: `${p.proyecto_name} - ${p.start_date}`,
          }))
        );
      })
      .catch((error) => {
        console.error("Error fetching projects:", error);
        setErrorMessage("Error fetching projects.");
      });

    // If we have a batch object (either for edit or clone), pre-fill the fields
    if (batch) {
      // For a clone, you might have already appended "(Cloned)" to the name
      // in the parent, but you could do it here if you prefer
      setBatchName(batch.batch_name || "");
      setBatchSecondName(batch.batch_second_name || "");
      setBatchSecondDate(batch.batch_second_date || "");

      const mappedProjects = (batch.subproyectos || []).map((p) => ({
        proyecto_id: p.subproyecto_id,
        revenue_percentage: p.revenue_percentage,
      }));
      setBatchProjects(mappedProjects);
      recalculateRemainingPercentage(mappedProjects);
    } else {
      // Create mode defaults
      setBatchName("");
      setBatchSecondName("");
      setBatchSecondDate("");
      setBatchProjects([]);
      setRemainingPercentage(100);
    }
  }, [batch]);

  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const addProjectToBatch = () => {
    setBatchProjects([
      ...batchProjects,
      { proyecto_id: null, revenue_percentage: 0 },
    ]);
  };

  const removeProjectFromBatch = (index) => {
    const updatedBatchProjects = batchProjects.filter((_, i) => i !== index);
    setBatchProjects(updatedBatchProjects);
    recalculateRemainingPercentage(updatedBatchProjects);
  };

  const updateBatchProject = (index, field, value) => {
    const updatedBatchProjects = [...batchProjects];
    updatedBatchProjects[index][field] = value;

    if (field === "revenue_percentage") {
      recalculateRemainingPercentage(updatedBatchProjects);
    }

    setBatchProjects(updatedBatchProjects);
  };

  const recalculateRemainingPercentage = (subprojects) => {
    const totalPercentage = subprojects.reduce(
      (sum, subproject) => sum + parseFloat(subproject.revenue_percentage || 0),
      0
    );
    setRemainingPercentage(100 - totalPercentage);
  };

  const submitBatch = (e) => {
    e.preventDefault();

    // Validate subprojects
    const validBatchProjects = batchProjects
      .filter(
        (subproject) =>
          subproject.proyecto_id && subproject.revenue_percentage > 0
      )
      .map((subproject) => ({
        subproyecto_id: subproject.proyecto_id,
        revenue_percentage: parseFloat(subproject.revenue_percentage, 10),
      }));

    const totalPercentage = validBatchProjects.reduce((sum, sp) => {
      return sum + parseFloat(sp.revenue_percentage || 0);
    }, 0);

    // Basic form validations
    if (!batchName.trim() || !batchSecondName.trim() || !batchSecondDate) {
      setErrorMessage("All fields are required.");
      return;
    }

    if (Math.abs(totalPercentage - 100) > 0.5) {
      setErrorMessage("The total percentage must equal approximately 100%.");
      return;
    }

    const headers = getAuthHeaders();

    // Payload to send to the backend
    const payload = {
      batch_name: batchName,
      batch_second_name: batchSecondName,
      batch_second_date: batchSecondDate,
      subproyectos: validBatchProjects,
    };

    // If we're cloning, we always do a POST because it's a new record
    // If we're editing, we do a PUT
    // If there's no batch at all, we're creating a new one (POST)
    let request;
    if (isCloneMode || !batch) {
      // Create/Clone => POST
      request = axios.post(API_URL_batch_subproyectos, payload, { headers });
    } else {
      // Edit => PUT
      request = axios.put(
        `${API_URL_batch_subproyectos}${batch.batch_code}/`,
        payload,
        { headers }
      );
    }

    request
      .then(() => {
        if (isCloneMode) {
          setSuccessMessage("Cloned new Batch successfully.");
        } else {
          setSuccessMessage(
            batch ? "Batch updated successfully." : "Batch created successfully."
          );
        }

        // Reset form state
        setBatchName("");
        setBatchSecondName("");
        setBatchSecondDate("");
        setBatchProjects([]);
        setRemainingPercentage(100);

        resetState(); // Refresh parent data
        toggle(); // Close modal
      })
      .catch((error) => {
        setErrorMessage(
          error.response?.data?.error ||
            "Error saving batch. Please try again."
        );
      });
  };

  return (
    <Form onSubmit={submitBatch}>
      <h4>{formTitle}</h4>
      {errorMessage && <Alert color="danger">{errorMessage}</Alert>}
      {successMessage && <Alert color="success">{successMessage}</Alert>}

      <FormGroup>
        <Label for="batchName">Nombre Bundle</Label>
        <Input
          type="text"
          id="batchName"
          value={batchName}
          onChange={(e) => setBatchName(e.target.value)}
          placeholder="Ingresa nombre de bundle"
        />
      </FormGroup>

      <FormGroup>
        <Label for="batchSecondName">Segundo Nombre</Label>
        <Input
          type="text"
          id="batchSecondName"
          value={batchSecondName}
          onChange={(e) => setBatchSecondName(e.target.value)}
          placeholder="Ingresa segundo nombre"
        />
      </FormGroup>

      <FormGroup>
        <Label for="batchSecondDate">Fecha</Label>
        <Input
          type="date"
          id="batchSecondDate"
          value={batchSecondDate}
          onChange={(e) => setBatchSecondDate(e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <Label>Proyectos en Bundle</Label>
        {batchProjects.map((batchProject, index) => (
          <div key={index} className="mb-3">
            <Select
              options={subprojects}
              value={
                subprojects.find(
                  (sp) => sp.value === batchProject.proyecto_id
                ) || null
              }
              onChange={(selectedOption) =>
                updateBatchProject(index, "proyecto_id", selectedOption.value)
              }
              placeholder="Selecciona subproyecto"
            />
            <Input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={batchProject.revenue_percentage}
              placeholder="Revenue Percentage"
              className="mt-2"
              onChange={(e) =>
                updateBatchProject(index, "revenue_percentage", e.target.value)
              }
            />
            <Button
              color="danger"
              size="sm"
              className="mt-2"
              onClick={() => removeProjectFromBatch(index)}
            >
              Remover
            </Button>
          </div>
        ))}
        <Button color="primary" onClick={addProjectToBatch}>
          Agregar Subprojecto
        </Button>
      </FormGroup>

      <p>Porcentaje pendiente: {remainingPercentage}%</p>
      <Button
        color="success"
        disabled={
          remainingPercentage !== 0 ||
          !batchName.trim() ||
          !batchSecondName.trim() ||
          !batchSecondDate
        }
      >
        Guardar
      </Button>
    </Form>
  );
};

export default NewBatchSubproyectoForm;